import React, { useState } from 'react';
import { Typography, Button, Select } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconDownload, IconCookie } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useUserProfile } from '../../../contextProviders/UserProfileProvider';
import { useHostProfile } from '../../../contextProviders/HostProfileProvider';
import { updateMyUserProfile, downloadMyDataExport } from '../../../services/UserProfileService';
import { updateMyHostProfile } from '../../../services/HostProfileService';
import DeleteAccountSection from '../../../components/DeleteAccountSection';
import { CookiePreferencesModal } from '../../../components/CookieConsent';
import SettingsCard from '../../../components/Settings/SettingsCard';
import SettingsToggleRow from '../../../components/Settings/SettingsToggleRow';
import SettingsRowShell from '../../../components/Settings/SettingsRowShell';
import { useAppBreakpoint } from '../../../hooks/useAppBreakpoint';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';

const { Title, Text } = Typography;

// Fixed options rather than a freeform number input -- avoids a "0 hours"/
// "9999 hours" footgun with zero extra validation code.
const QUOTE_RESPONSE_WINDOW_HOUR_OPTIONS = [1, 2, 4, 8, 12, 24];

// The guest's side of the same negotiation, in days rather than hours: this
// one is measured in "they'll get to it tomorrow", not "they're at the
// screen now". Wider ceiling than the host's own window because the cost of
// a long one falls on the host, who is the one choosing it.
const QUOTE_GUEST_WINDOW_DAY_OPTIONS = [1, 2, 3, 5, 7, 14];

// Must match QuoteWindowPolicy on the API, which is what actually decides the
// deadline -- this screen's only job is to show what that will be.
const DEFAULT_QUOTE_RESPONSE_WINDOW_HOURS = 24;
const DEFAULT_QUOTE_GUEST_WINDOW_HOURS = 48;

/**
 * The window the server will really use, which is not always the number in the
 * column.
 *
 * `?? 48` was the wrong guard and quietly broke this screen: it only fires for
 * null/undefined, and the value that actually needed catching was 0 -- what a
 * migration backfills into a new non-nullable int column, whatever the
 * entity's own default says. 0 sailed through, the select had no option for
 * it, and the row rendered a bare unlabelled number. So the one host whose
 * quotes were being cancelled the instant they countered was looking at a
 * settings screen that showed them nothing wrong.
 */
const usableWindowHours = (configured: number | undefined, fallback: number): number =>
  typeof configured === 'number' && configured > 0 ? configured : fallback;

/**
 * The options list, guaranteed to contain the value being displayed.
 *
 * An antd Select handed a value no option matches renders the raw value with
 * no label, which reads as a glitch rather than as a setting. A window set
 * through some other path (an admin edit, a hand-run UPDATE) is still a real
 * window and should show as one.
 */
const withCurrent = (options: number[], current: number): number[] =>
  options.includes(current) ? options : [...options, current].sort((a, b) => a - b);

/** "Configuración > Privacidad" -- messages, published spaces discoverability, review visibility, GDPR. */
const PrivacySection: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { userProfile, refresh: refreshUserProfile } = useUserProfile();
  const { hostProfile, hasHostProfile, refresh: refreshHostProfile } = useHostProfile();
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [cookieModalOpen, setCookieModalOpen] = useState(false);

  const handleUserProfileToggle = async (key: string, value: boolean) => {
    setSavingKey(key);
    try {
      await updateMyUserProfile({ [key]: value });
      await refreshUserProfile();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.PROFILE_UPDATED_MESSAGE));
    } finally {
      setSavingKey(null);
    }
  };

  const handleHostProfileToggle = async (value: boolean) => {
    setSavingKey('includeInSearchEngines');
    try {
      await updateMyHostProfile({ includeInSearchEngines: value });
      await refreshHostProfile();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.PROFILE_UPDATED_MESSAGE));
    } finally {
      setSavingKey(null);
    }
  };

  const handleQuoteResponseWindowChange = async (value: number) => {
    setSavingKey('quoteResponseWindowHours');
    try {
      await updateMyHostProfile({ quoteResponseWindowHours: value });
      await refreshHostProfile();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.PROFILE_UPDATED_MESSAGE));
    } finally {
      setSavingKey(null);
    }
  };

  const handleDeferredCaptureToggle = async (checked: boolean) => {
    setSavingKey('deferredCaptureEnabled');
    try {
      await updateMyHostProfile({ deferredCaptureEnabled: checked });
      await refreshHostProfile();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.PROFILE_UPDATED_MESSAGE));
    } finally {
      setSavingKey(null);
    }
  };

  const handleQuoteGuestWindowChange = async (days: number) => {
    setSavingKey('quoteGuestWindowHours');
    try {
      // Stored in hours server-side (it shares the deadline plumbing with the
      // host's own window); offered in days because that's the unit it's
      // actually reasoned about in.
      await updateMyHostProfile({ quoteGuestWindowHours: days * 24 });
      await refreshHostProfile();
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.PROFILE_UPDATED_MESSAGE));
    } finally {
      setSavingKey(null);
    }
  };

  // Resolved once, and shown exactly as the server will read them -- see
  // usableWindowHours.
  const responseWindowHours = usableWindowHours(hostProfile?.quoteResponseWindowHours, DEFAULT_QUOTE_RESPONSE_WINDOW_HOURS);
  const guestWindowDays = Math.max(
    1,
    Math.round(usableWindowHours(hostProfile?.quoteGuestWindowHours, DEFAULT_QUOTE_GUEST_WINDOW_HOURS) / 24)
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadMyDataExport();
      message.success(translate(t, TRANSLATIONS.SETTINGS_GDPR_EXPORT_SUCCESS));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.SETTINGS_GDPR_EXPORT_ERROR));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      {/* Redundant on mobile -- the list->detail header (Settings.tsx) already shows this section's name. */}
      {!isMobile && (
        <Title level={4} style={{ marginBottom: 20 }}>
          {translate(t, TRANSLATIONS.SETTINGS_NAV_PRIVACY)}
        </Title>
      )}

      <SettingsCard title={translate(t, TRANSLATIONS.SETTINGS_PRIVACY_MESSAGES_SECTION)}>
        <SettingsToggleRow
          title={translate(t, TRANSLATIONS.SETTINGS_PRIVACY_READ_RECEIPTS_TITLE)}
          description={translate(t, TRANSLATIONS.SETTINGS_PRIVACY_READ_RECEIPTS_DESCRIPTION)}
          checked={userProfile?.showReadReceipts ?? true}
          loading={savingKey === 'showReadReceipts'}
          onChange={(checked) => handleUserProfileToggle('showReadReceipts', checked)}
        />
      </SettingsCard>

      {hasHostProfile && (
        <SettingsCard title={translate(t, TRANSLATIONS.SETTINGS_PRIVACY_SPACES_SECTION)}>
          <SettingsToggleRow
            title={translate(t, TRANSLATIONS.SETTINGS_PRIVACY_SEARCH_INDEXABLE_TITLE)}
            description={translate(t, TRANSLATIONS.SETTINGS_PRIVACY_SEARCH_INDEXABLE_DESCRIPTION)}
            checked={hostProfile?.includeInSearchEngines ?? true}
            loading={savingKey === 'includeInSearchEngines'}
            onChange={handleHostProfileToggle}
          />
          {/* HIDDEN, not disabled, when the platform is not offering this --
              a toggle a host can move that changes nothing is worse than no
              toggle at all. See IHostProfile.deferredCaptureAvailable. */}
          {hostProfile?.deferredCaptureAvailable && (
            <SettingsToggleRow
              title={translate(t, TRANSLATIONS.HOST_DEFERRED_CAPTURE_LABEL)}
              description={translate(t, TRANSLATIONS.HOST_DEFERRED_CAPTURE_HELP)}
              checked={hostProfile?.deferredCaptureEnabled ?? false}
              loading={savingKey === 'deferredCaptureEnabled'}
              onChange={handleDeferredCaptureToggle}
            />
          )}
          <SettingsRowShell
            left={
              <div>
                <Text strong style={{ fontSize: 15 }}>
                  {translate(t, TRANSLATIONS.SETTINGS_PRIVACY_QUOTE_RESPONSE_WINDOW_TITLE)}
                </Text>
                <div style={{ marginTop: 2 }}>
                  <Text type="secondary">{translate(t, TRANSLATIONS.SETTINGS_PRIVACY_QUOTE_RESPONSE_WINDOW_DESCRIPTION)}</Text>
                </div>
              </div>
            }
            right={
              <Select
                value={responseWindowHours}
                loading={savingKey === 'quoteResponseWindowHours'}
                onChange={handleQuoteResponseWindowChange}
                style={{ width: 120 }}
                options={withCurrent(QUOTE_RESPONSE_WINDOW_HOUR_OPTIONS, responseWindowHours).map((hours) => ({
                  value: hours,
                  label: translate(t, TRANSLATIONS.SETTINGS_PRIVACY_QUOTE_RESPONSE_WINDOW_HOURS, { hours }),
                }))}
              />
            }
          />
          <SettingsRowShell
            left={
              <div>
                <Text strong style={{ fontSize: 15 }}>
                  {translate(t, TRANSLATIONS.SETTINGS_PRIVACY_QUOTE_GUEST_WINDOW_TITLE)}
                </Text>
                <div style={{ marginTop: 2 }}>
                  <Text type="secondary">{translate(t, TRANSLATIONS.SETTINGS_PRIVACY_QUOTE_GUEST_WINDOW_DESCRIPTION)}</Text>
                </div>
              </div>
            }
            right={
              <Select
                value={guestWindowDays}
                loading={savingKey === 'quoteGuestWindowHours'}
                onChange={handleQuoteGuestWindowChange}
                style={{ width: 120 }}
                options={withCurrent(QUOTE_GUEST_WINDOW_DAY_OPTIONS, guestWindowDays).map((days) => ({
                  value: days,
                  label: translate(t, TRANSLATIONS.SETTINGS_PRIVACY_QUOTE_GUEST_WINDOW_DAYS, { days }),
                }))}
              />
            }
          />
        </SettingsCard>
      )}

      <SettingsCard title={translate(t, TRANSLATIONS.SETTINGS_PRIVACY_REVIEWS_SECTION)}>
        <SettingsToggleRow
          title={translate(t, TRANSLATIONS.SETTINGS_PRIVACY_SHOW_LOCATION_TITLE)}
          description={translate(t, TRANSLATIONS.SETTINGS_PRIVACY_SHOW_LOCATION_DESCRIPTION)}
          checked={userProfile?.defaultShowReviewerLocation ?? true}
          loading={savingKey === 'defaultShowReviewerLocation'}
          onChange={(checked) => handleUserProfileToggle('defaultShowReviewerLocation', checked)}
        />
        <SettingsToggleRow
          title={translate(t, TRANSLATIONS.SETTINGS_PRIVACY_SHOW_DURATION_TITLE)}
          description={translate(t, TRANSLATIONS.SETTINGS_PRIVACY_SHOW_DURATION_DESCRIPTION)}
          checked={userProfile?.defaultShowStayDuration ?? true}
          loading={savingKey === 'defaultShowStayDuration'}
          onChange={(checked) => handleUserProfileToggle('defaultShowStayDuration', checked)}
        />
      </SettingsCard>

      <SettingsCard title={translate(t, TRANSLATIONS.SETTINGS_PRIVACY_GDPR_SECTION)}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Button shape="round" icon={<IconDownload size={16} />} loading={exporting} onClick={handleExport} style={{ alignSelf: 'flex-start' }}>
            {translate(t, TRANSLATIONS.SETTINGS_PRIVACY_REQUEST_DATA_BUTTON)}
          </Button>
          <Button shape="round" icon={<IconCookie size={16} />} onClick={() => setCookieModalOpen(true)} style={{ alignSelf: 'flex-start' }}>
            {translate(t, TRANSLATIONS.SETTINGS_PRIVACY_COOKIE_PREFERENCES_BUTTON)}
          </Button>
        </div>
      </SettingsCard>

      <DeleteAccountSection />

      <CookiePreferencesModal open={cookieModalOpen} onClose={() => setCookieModalOpen(false)} />
    </div>
  );
};

export default PrivacySection;
